// Global
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_WIDTH = '100%';
const DEFAULT_HEIGHT = 'auto';

type UseIframeWidthProps = {
  width?: string | number;
  height?: string;
};

export const useIframeWidth = ({ width, height }: UseIframeWidthProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportWidth = containerRef.current?.clientWidth;

  const finalWidth = width || DEFAULT_WIDTH;
  const finalHeight = height || DEFAULT_HEIGHT;

  const calculateWidth = useCallback(
    (widthValue: string | number) => {
      const numericWidth = typeof widthValue === 'string' ? parseInt(widthValue) : widthValue;
      if (
        (containerRef.current?.clientWidth &&
          numericWidth &&
          numericWidth > containerRef.current?.clientWidth) ||
        !width
      ) {
        return '100%';
      }
      return widthValue;
    },
    [width]
  );

  const [calculatedWidth, setCalculatedWidth] = useState(() => calculateWidth(finalWidth));
  const isFullWidth = calculatedWidth === DEFAULT_WIDTH;
  const calculatedHeight = finalHeight;

  useEffect(() => {
    const handleResize = () => {
      setCalculatedWidth(calculateWidth(finalWidth));
    };

    // Call immediately to ensure correct initial width
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [finalWidth, viewportWidth, calculateWidth]);

  return {
    containerRef,
    calculatedWidth,
    calculatedHeight,
    isFullWidth,
  };
};
